import * as React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import MainHeader from '../../../headers/MainHeader/MainHeader';
import * as actions from '../../../../redux/corpus/actions.corpus';
import * as projectActions from '../../../../redux/projects/actions.projects';
import { IProject } from '../../../../models/IProject';
import CorpusPageHeader from '../CorpusPageHeader/CorpusPageHeader';
import CorpusDocumentsPanel from '../CorpusDocumentsPanel/CorpusDocumentsPanel';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import getProjectId from '../../../../services/mini/getProjectId';
import { IGetCorpusDocumentsParam, ISelectCorpusDocumentParam } from '../../../../redux/corpus/actions.corpus';
import { IReconnectProjectParam } from '../../../../redux/projects/saga.projects';
import { IStore } from '../../../../redux/initialState';
import CorpusDocumentBody from '../CorpusDocumentBody/CorpusDocumentBody';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import NoContentView from '../../../components/NoContentView/NoContentView';
import { Environment } from '../../../../const/environment';
import projectHelper from '../../../../services/ProjectHelper';
import HistoryKeeper from '../../../../services/HistoryKeeper';
import { getLocalStorageValue, setLocalStorageValue } from '../../../../services/mini/storage';
const { default: noChangesIcon } = require('../../../../assets/images/diff/no-changes-script-pic@2x.png');

type CorpusPageProps = PropsFromRedux & {
    environment: Environment
    project: IProject
    projectId: string,
    onClose?: () => void,
}

type CorpusPageState = {
    documents: any[]
    isLoading: boolean
    selectedFormat: string
    hasData: boolean
}

const CORPUS_FORMAT_KEY = 'alan-corpus-format';

class CorpusPage extends React.Component<CorpusPageProps, CorpusPageState> {
    _isMounted: boolean;

    constructor(props) {
        super(props);
        this._isMounted = false;

        this.state = {
            documents: null,
            isLoading: false,
            selectedFormat: getLocalStorageValue(CORPUS_FORMAT_KEY, 'text'),
            hasData: false
        };
    }

    componentDidMount() {
        const {
            project,
            projectId,
            environment,
            getProject
        } = this.props;

        this._isMounted = true;

        HistoryKeeper.push();

        if (!project?.name) {
            getProject({ projectId, environment });
        }

        this.fillData();
    }

    componentDidUpdate(): void {
        this.fillData();
    }

    componentWillUnmount(): void {
        const {
            clearCorpusInfo
        } = this.props;
        this._isMounted = false;
        clearCorpusInfo({});
    }

    fillData = async () => {
        const {
            corpusId,
            isProjectConnected,
            getCorpusDocuments,
            corpusLine,
            corpusTitle
        } = this.props;

        if (isProjectConnected && !this.state.isLoading && !this.state.hasData) {
            this.setState({ isLoading: true });
            getCorpusDocuments({
                corpusLine,
                corpusId,
                corpusTitle
            }).then((corpusDocs) => {
                if (this._isMounted) {
                    this.setState({ documents: corpusDocs, isLoading: false, hasData: true });
                }
            });
        }
    }

    selectDocument = (doc) => {
        const {
            corpusId,
            selectCorpusDocument
        } = this.props;

        if (corpusId && doc?.id) {
            selectCorpusDocument({ document: doc, corpusId });
        }
    }

    selectDocumentInSearch = (doc) => {
        const {
            corpusId,
            selectDocumentInSearch
        } = this.props;

        if (corpusId && doc?.id) {
            selectDocumentInSearch({ document: doc, corpusId });
        }
    }

    onFormatChange = () => {
        const { selectedFormat } = this.state;
        const newFormat = selectedFormat === 'text' ? 'markdown' : 'text'
        setLocalStorageValue(CORPUS_FORMAT_KEY, newFormat)
        this.setState({ selectedFormat: newFormat });
    }

    render() {
        const {
            environment,
            project,
            corpusId,
            corpusFile,
            corpusTitle,
            corpusLine,
            corpusSearchQuery,
            corpusSearchQustion,
            corpusMaxTokens,
            selectedDocument,
            searchedDocuments,
            searchInProcess,
            onClose
        } = this.props;
        const {
            documents,
            isLoading,
            selectedFormat
        } = this.state;
        return (
            <div className="corpus-page">
                <MainHeader environment={environment}>
                    <CorpusPageHeader
                        label={corpusId ? (corpusTitle || `${corpusFile || 'Corpus'}, line ${corpusLine}`) : ''}
                        environment={environment}
                        project={project}
                        selectedFormat={selectedFormat}
                        selectedDocument={selectedDocument}
                        onFormatChange={this.onFormatChange}
                        onClose={onClose}
                    />
                </MainHeader>
                <div className="corpus-page__content">

                    {(documents?.length > 0 || searchedDocuments?.length > 0) &&
                        <CorpusDocumentsPanel
                            key={`${corpusSearchQuery}-${corpusSearchQustion}-${corpusMaxTokens}`}
                            documents={searchedDocuments || documents}
                            isSearchActive={!!(corpusSearchQuery || corpusSearchQustion)}
                            isLoading={isLoading}
                            searchInProcess={searchInProcess}
                            changeSelectedDocument={this.selectDocument}
                            changeSelectedDocumentInSearch={this.selectDocumentInSearch}
                            selectedDocument={selectedDocument} />}


                    {documents === null ?
                        <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} center={true} appearWithFade={true} /> :
                        <>
                            {documents.length === 0 ?
                                <NoContentView img={noChangesIcon} label="No documents were found for this corpus." /> :
                                <CorpusDocumentBody selectedFormat={selectedFormat} />}
                        </>
                    }

                </div>
            </div>
        );

    }
}
function mapStateToProps({ projectsInfo, corpus }: IStore, ownProps) {
    const projectId = getProjectId(ownProps.projectName || ownProps.params.projectName, projectsInfo.projects);
    const environment = projectHelper.getEnvironment(projectId);

    const corpusLine = ownProps?.params?.corpusLine;
    const corpusId = ownProps.corpusId || corpus.corpusId;
    const corpusTitle = ownProps.corpusTitle || corpus.title;

    return {
        environment,
        corpusId,
        corpusTitle,
        corpusFile: corpus.file,
        corpusSearchQuery: corpus.corpusSearchQuery,
        corpusSearchQustion: corpus.corpusSearchQustion,
        selectedDocument: corpus.selectedDocument,
        projectId,
        project: projectsInfo.currentProject,
        searchedDocuments: corpus.searchedDocuments,
        corpusMaxTokens: corpus.corpusMaxTokens,
        isProjectConnected: projectsInfo.currentProject.isProjectConnected,
        corpusLine,
        searchInProcess: corpus.searchInProcess,
    };
}

function mapDispatchToProps(dispatch) {


    return {
        reconnectProject: (param: IReconnectProjectParam) => dispatch(projectActions.reconnectProject(param)),
        getProject: (param) => dispatch(projectActions.getProject(param)),
        clearCorpusInfo: (param) => dispatch(actions.clearCorpusInfo(param)),
        getCorpusDocuments(param: IGetCorpusDocumentsParam): Promise<any> {
            return asyncDispatch(actions.getCorpusDocuments, dispatch, param);
        },
        selectCorpusDocument(param: ISelectCorpusDocumentParam): Promise<any> {
            return asyncDispatch(actions.selectCorpusDocument, dispatch, param);
        },
        selectDocumentInSearch(param: ISelectCorpusDocumentParam): Promise<any> {
            return asyncDispatch(actions.selectCorpusDocumentInSearch, dispatch, param);
        },
    };
}


const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CorpusPage);
